import * as React from 'react';
import { loadUniversityList_CTSAReport } from '../../../actions/UserActions';
import { UserState } from '../../../interface/MyInterfaces';
import { VendorReportState, VendorReportAction, UserVendorReportState } from '../../../interface/VendorReportInterface';
import { withUserActionTracker } from '../../../sharedUtilities/UserActionTracker';
import { isAgg } from '../../Common/Helpers';
import { ReportText, TextLink, TextSpan } from "../../Common/SharedStyledComponents";

interface extraProp {
    hasCensusData: boolean
}

class MethodologyTab extends React.Component<UserState & VendorReportState & extraProp & VendorReportAction & UserVendorReportState, {}> {
    constructor(props) {
        super(props);
    }

    public render() {
        const uniName = this.props.agg ? this.props.HomeState.campusname.replace('Member', 'member').replace('Universities', 'universities') : `your university`;
        const uniNameWithS = uniName + (this.props.agg ? '’' : '’s')
        let about_the_data = ""
        if (this.props.display_id == "999067") {
            about_the_data = "This report includes data from universities that are members of IRIS and that have reported to IRIS the relevant data for calendar years 2020-2022: Boston University, Cornell University, Emory University, Georgia Tech, Michigan State University, New York University, Ohio State University, Princeton University, Rutgers University, Stony Brook University, Texas A&M, Texas State University, The University of Texas at Arlington, The University of Texas at Dallas, The University of Texas at San Antonio, University of California - Riverside, University of Illinois at Urbana-Champaign, University of Kansas, University of Kentucky, University of Michigan, University of Pittsburgh, University of Utah, University of Wisconsin, and Wayne State University."
        }
        else if (this.props.display_id == "999065") {
            about_the_data = "This report includes data from members of Michigan’s University Research Corridor (URC) that have reported to IRIS the relevant data for calendar years 2018-2022: Michigan State University, University of Michigan, and Wayne State University."
        }
        else if (this.props.display_id == "999066") {
            about_the_data = "This report includes data from universities belonging to Texas' Council of Public University Presidents and Chancellors (CPUPC) that are members of IRIS and that have reported to IRIS the relevant data for calendar years 2020-2022: Texas A&M, Texas State University, The University of Texas at Arlington, The University of Texas at Dallas, and The University of Texas at San Antonio.\n"
        }

        return <div className='tabcontents container-fluid'>
            <div className='row'>
                <div className='col-md-12'>
                    <h3>Vendor Profile Report Methodology</h3>
                    <h3 className="subtitle">Details on how the reports are created, what they show, and the process of
                        matching different datasets</h3>
                    <h5 className="levelOneHeader">Introduction</h5>
                    <p>
                        The IRIS Vendor Profile Report demonstrates the economic impact of universities’ research
                        spending by describing the industries, geographic areas, and types of businesses that receive
                        contracts and subawards for goods and services through research grants. The report shows the
                        dollars of research funding spent; the number and types of establishments receiving research
                        spending, including small businesses and minority-owned and women-owned businesses; and whether
                        these
                        figures change from year to year. Data include both federal and non-federal research
                        expenditures.
                    </p>

                    {this.props.agg ? (
                        <>
                            <h5 className="levelOneHeader">ABOUT THE DATA</h5>
                            <p>{about_the_data}</p>
                        </>
                    ) : null}

                    <h5 className="levelOneHeader">How to understand the results</h5>

                    <h6 className="levelTwoHeader">The Vendor Profile tab</h6>
                    <p>
                        This section provides an overview of the economic impacts of research spending by describing the
                        direct expenditures of {uniNameWithS} research funding to outside vendors and subcontractors.
                        This section shows such spending at the national level, including expenditures to minority-owned
                        and
                        women-owned businesses, and small businesses.
                    </p>
                    <p>
                        Selection of Research Spending or Establishments affects both the line graphs and the radar
                        chart. Yearly spending, including both vendor spending and subawards, is included on the line
                        graphs for every year for which {this.props.agg ? `universities` : `your university has`} sent
                        complete data. Spending is displayed separately for women-owned businesses, minority-owned
                        businesses, and small businesses.
                        When using the radar chart, the user must select an individual year to display. The radar chart
                        shows how the percentages of all research spending
                        that {uniName} {this.props.agg ? `spend` : `spends`} with small businesses, minority-owned businesses, and
                        women-owned businesses compare with all other IRIS universities with these types of businesses
                        in that year.
                    </p>
                    <h6 className="levelTwoHeader">The Industry Revenue Analysis tab</h6>
                    <p>
                        The sunburst display shows spending organized by industry, using NAICS codes (see below for a
                        description of NAICS codes). In this visualization, NAICS codes are assigned to
                        multi-establishment firms based on the NAICS of the establishment(s) with the largest revenue.
                        Thus, this display shows the impact of the dollars {uniName} spends on industries, as
                        characterized by their most typical revenue stream. You can view spending by
                        NAICS industry sector, sub-sector, or group. It is important to note the spending numbers we
                        report for each industry do not include expenditures to establishments/firms that were not
                        matched. Please see the Data Matching section below for more detail on the data underlying this
                        report.
                    </p>
                    <h5 className="levelOneHeader">How the reports are created</h5>
                    <p>
                        This report is constructed by linking the vendor and subawardee
                        data {this.props.agg ? `supplied by participating ` + uniName : `your university supplies`} to
                        IRIS with business directory data. Prior to any
                        data linking, vendor and subawardee names for individual people are suppressed by IRIS. Name
                        standardization is used to correct common data entry errors in vendor names. Quality rules, such
                        as requiring the disambiguated vendor name to be correct and the date of the transaction to be
                        reasonable, are applied.
                    </p>
                    <p>
                        A file of vendor and subawardee names and addresses is submitted and matched with our data (no personally identifiable data about individuals are shared). Their
                        matching procedures link {uniNameWithS} vendor and subawardee names and address
                        data to their database, testing each potential match and giving it a quality rating of A-E, with
                        A being the highest quality and E being the lowest. In this report, IRIS uses all matches with a
                        grade of “A” or “B”.
                        IRIS also uses a subset of records that have received a “C”, “D”, or “E”, rating. In order to
                        determine whether or not these records should be included, vendor names and location information
                        are compared against the returned name and location using a token-sorted Levenshtein distance.
                        If the match score for each field exceeds our threshold, the match is verified and included in
                        the report. In addition, unmatched records indicating subawards or purchases larger than $1
                        million were reviewed by IRIS staff. These records were matched to businesses when IRIS staff
                        found additional information on company names or locations that could be used to overrule the
                        automated match guidelines. The data retrieved from these matches includes measures of industry
                        (NAICS) based on revenue.
                    </p>

                    <p>
                        All spending metrics are expressed in real {this.props.BVDMatchRates.inflation_year} dollars.
                    </p>
                    <h6 className="levelTwoHeader">NAICS Codes</h6>
                    <p>The North American Industry Classification System (NAICS) is a system for classifying
                        establishments (individual business locations) by type of economic activity in Canada, Mexico,
                        and the United States. Its purposes are: (1) to facilitate the collection, tabulation,
                        presentation, and analysis of data relating to establishments, and (2) to promote uniformity and
                        comparability in the presentation and analysis of statistical data describing the North American
                        economy. NAICS is used by Federal statistical agencies that collect or publish data by industry.
                        For this report, NAICS codes are used to classify businesses according to the types of economic
                        production in which they participate.
                        The codes are organized into levels beginning with broad industry sectors to more specific
                        groupings.
                        The first two digits of a NAICS code represent general categories of economic activities.
                        Nested within the two-digit codes, a third digit represents the subsector. Nested in turn within
                        that, a fourth digit represents the industry group.
                        IRIS matches payments to vendors and subawardees at the four-digit NAICS level. NOTE: For this
                        report, we use the 2022 NAICS codes for the first time.
                        The codes are revised every five years. The retail and publishing sectors saw some of the most
                        significant changes in the latest revision.
                        A summary of the differences between the 2022 and 2017 codes can be found at <a
                            href="https://www.bls.gov/sae/additional-resources/details-on-the-conversion-to-the-2022-north-american-industry-classification-system-naics-from-2017-naics.htm">
                            https://www.bls.gov/sae/additional-resources/details-on-the-conversion-to-the-2022-north-american-industry-classification-system-naics-from-2017-naics.htm </a>
                    </p>

                    <h6 className="levelTwoHeader">Data Matching</h6>
                    <p>IRIS links {uniNameWithS} vendor and subaward data with the U.S. Census Bureau's data
                        resources to return summaries by year, North American Industry Classification System
                        (NAICS) code, and business ownership.</p>
                    <p>Matching vendor and subcontractor information to business data is a multi-step process that
                        starts with the vendor and subawardee name and address information submitted by
                        universities.</p>

                    <h6 className="levelThreeHeader">Upload and Data Cleaning</h6>
                    <p className="indentOne">In the first step, a university uploads data to IRIS. Next, IRIS
                        standardizes the names and addresses of vendors and subawardees, creates a list of unique
                        vendors / subawardees, and removes the names of all individual people from that list. For this
                        report, IRIS also imposes a requirement that the vendor or subawardee be in the United States.
                        IRIS performs these data cleaning steps every time we prepare for the next step, matching to
                        data sources.</p>
                    <h6 className="levelThreeHeader">Matching</h6>
                    <p className="indentOne">In this step, the list of standardized names and addresses
                        for {uniNameWithS} vendors and subawardees is matched.</p>

                    <h6 className="levelThreeHeader levelFourHeader">Matching to our business Dataset</h6>
                    <p className="indentTwo">
                        {this.props.VendorReport_MatchRateTab && this.props.VendorReport_MatchRateTab.bvd_match}

                    </p>

                    <h5 className="levelOneHeader">Limitations</h5>
                    <p>Only vendors and subawardees with U.S. addresses are included in this report. This is done to
                        ensure a consistent basis across all metrics, since some metrics, such as NAICS codes, cannot be
                        applied to spending in many other countries.</p>
                    <p>A vendor address in administrative records may be a firm's out-of-state billing address rather
                        than the address of a local establishment that provided a service. To the extent that this
                        occurs, local spending will be underestimated because the spending will be attributed to the
                        out-of-state billing address.</p>
                    <p>We continue to work to improve the quality of matching and data production in order to address
                        these limitations.</p>

                    <p>
                        The data underlying this report represents research funding from federal and non-federal
                        sources. You may download the underlying data by clicking the “Download Data” link at the top
                        and bottom of the report. The report can be shared by clicking the “Share Report” link at the
                        top of the report.
                    </p>

                    <h5 className="levelOneHeader">Citation: </h5>
                    <p>Vendor Profile Report, Institute for Research on Innovation and Science
                        (IRIS), {this.props.vendorRelease}</p>


                    <ReportText center>
                        <TextSpan bold>Website: </TextSpan><TextLink href="https://iris.isr.umich.edu/"
                            target="_blank">iris.isr.umich.edu</TextLink> |
                        <TextSpan bold> Email: </TextSpan><TextLink href="mailto:IRIS-info@umich.edu"
                            target="_blank">IRIS-info@umich.edu</TextLink> |
                        <TextSpan bold> LinkedIn: </TextSpan><TextLink href="https://www.linkedin.com/company/iris-um"
                            target="_blank">linkedin.com/company/iris-um</TextLink>
                    </ReportText>
                </div>
            </div>
        </div>
    }
}

export default withUserActionTracker(MethodologyTab, "Vendor Profile Report", "viewed Methodology Tab");